import React, { useState, useEffect, useMemo } from 'react'
import { ButtonHLPure, InteractiveDialog, LabelHLPure, SearchBarHLPure, GridSelectionHLPure, CheckBoxHLPure, ButtonAddHLPure, InputHLPure } from '../index'
import './DocumentsToRequireClassifyModal.pure.css'
import { useTranslation } from 'react-i18next'

/**
 * @function DocumentsToRequireClassifyModal
 * Modal reutilizable para clasificar documentos en grupos
 * @param {{
 *   showModal: boolean,
 *   onClose: function,
 *   onSave: function,
 *   initialSelectedGroups: Array,
 *   availableGroups: Array,
 *   canCreateGroups: boolean,
 *   onCreateGroup: function
 * }} props
 * @returns {JSX.Element | null}
 */
const DocumentsToRequireClassifyModal = ({
  onSave,
  onClose,
  showModal,
  onCreateGroup,
  availableGroups = [],
  canCreateGroups = false,
  initialSelectedGroups = [],
  showLoadingRequesting = false,
  showCompletedRequesting = false
}) => {

  const { t } = useTranslation()
  const [newGroupName, setNewGroupName] = useState('')
  const [groupSearchText, setGroupSearchText] = useState('')
  const [groupNameError, setGroupNameError] = useState(false)
  const [tempSelectedGroups, setTempSelectedGroups] = useState([])

  useEffect(() => {
  }, [availableGroups, showModal])

  useEffect(() => {
    if (showModal) {
      setTempSelectedGroups(initialSelectedGroups)
      setGroupSearchText('')
    }
  }, [showModal, initialSelectedGroups])

  /**
   * @function filteredGroups
   * Filtra grupos por búsqueda
   * @returns {Array}
   */
  const filteredGroups = useMemo(() => {
    if (!groupSearchText.trim()) return availableGroups
    return availableGroups.filter(group =>
      group.name.toLowerCase().includes(groupSearchText.toLowerCase())
    )
  }, [availableGroups, groupSearchText])

  /**
   * @function gridGroups
   * Marca grupos como seleccionados para el grid
   * @returns {Array}
   */
  const gridGroups = useMemo(() => {
    return filteredGroups.map(group => ({
      ...group,
      selected: tempSelectedGroups.some(selected => selected.id === group.id)
    }))
  }, [filteredGroups, tempSelectedGroups])

  /**
   * @function handleToggleGroup
   * Alterna la selección de un grupo desde el checkbox
   * @param {Object} event - Evento del checkbox
   * @param {Object} dataItem - Grupo a alternar
   * @returns {void}
   */
  const handleToggleGroup = (event, dataItem) => {
    if (tempSelectedGroups.some(g => g.id === dataItem.id)) {
      setTempSelectedGroups(tempSelectedGroups.filter(g => g.id !== dataItem.id))
    } else {
      setTempSelectedGroups([...tempSelectedGroups, dataItem])
    }
  }

  /**
   * @function handleSaveGroups
   * Guarda los grupos seleccionados y cierra el modal
   * @returns {void}
   */
  const handleSaveGroups = () => {
    onSave(tempSelectedGroups)
    onClose()
  }

  /**
   * @function handleCloseModal
   * Cierra el modal sin guardar cambios
   * @returns {void}
   */
  const handleCloseModal = () => {
    setGroupSearchText('')
    onClose()
  }

  /**
   * @function handleGroupSearchChange
   * Maneja el cambio en el input de búsqueda
   * @param {Object} e - Evento del input
   * @returns {void}
   */
  const handleGroupSearchChange = (e) => {
    setGroupSearchText(e.target.value)
  }

  /**
   * @function handleCreateGroup
   * Crea un nuevo grupo y lo agrega a la lista
   * @returns {void}
   */
  const handleCreateGroup = async () => {
    const name = newGroupName.trim()

    if (!name) {
      setGroupNameError(true)
      return
    }

    const exists = availableGroups.some(
      g => g.name.toLowerCase() === name.toLowerCase()
    )

    if (exists) {
      setGroupNameError(true)
      return
    }

    const newGroup = {
      id: Date.now(), // dummy
      name,
      selected: false
    }

    setNewGroupName('')
    setGroupNameError(false)

    if (onCreateGroup) {
      await onCreateGroup(newGroup)
    }
  }


  /**
   * @function cellColumnOne
   * Cell personalizado para la columna del grid con checkbox
   * @param {Object} props - Props del cell
   * @returns {JSX.Element}
   */
  const cellColumnOne = (props) => {
    return (
      <td>
        <div className='documentsToRequireClassifyModal-cell-container'>
          <CheckBoxHLPure
            valueCheckBox={props.dataItem.id}
            idCheckBox={props.dataItem.id}
            onChangeHandler={(e) => handleToggleGroup(e, props.dataItem)}
            checkedState={props.dataItem.selected || false}
          />
          <span className='documentsToRequireClassifyModal-group-name'>
            {props.dataItem.name}
          </span>
        </div>
      </td>
    )
  }


  if (!showModal || showLoadingRequesting || showCompletedRequesting) return null

  return (
    <InteractiveDialog
      onClose={handleCloseModal}
      title={t('documentsToRequire.modal.title.group.document')}
      dialogSize='M'
    >
      <div className='documentsToRequireClassifyModal-modal-content'>
        {canCreateGroups && (
          <div className="documentsToRequireClassifyModal-create-group">
            <LabelHLPure text={t('documentsToRequire.modal.create.new.group')} />
            <div className="documentsToRequireClassifyModal-create-group-actions">
              <div className='documentsToRequireClassifyModal-input-group-name'>
                <InputHLPure
                  valueInput={newGroupName}
                  tooltipShow={true}
                  inputActive
                  handleChangeInput={(e) => {
                    setNewGroupName(e.target.value)
                    setGroupNameError(false)
                  }}
                  errorInputText={t('documentsToRequire.hintErrorMesaggeInDocument')}
                />
              </div>

              <ButtonAddHLPure
                nameBtn={t('documentsToRequire.modal.create.group')}
                iconAdd
                onClickBtn={handleCreateGroup}
              />

            </div>

            {groupNameError && (
              <p className="labelErrorPure-label">
                {!newGroupName.trim()
                  ? t('documentsToRequire.modal.create.group.name')
                  : t('documentsToRequire.modal.create.group.exists')
                }
              </p>
            )}
          </div>
        )}

        <LabelHLPure text={t('documentsToRequire.modal.select.groups')} />
        <div className='documentsToRequireClassifyModal-search-container'>
          <SearchBarHLPure
            placeholder={t('button.search')}
            onSearchChange={handleGroupSearchChange}
            searchValue={groupSearchText}
          />
        </div>
        <GridSelectionHLPure
          skip={0}
          take={4}
          dataGrid={gridGroups}
          cellColumnOne={cellColumnOne}
          widthColumnOne='auto'
          pageable={true}
          styleGeneral
        />
        <div className='documentsToRequireClassifyModal-button-dialog'>
          <ButtonHLPure
            secondary
            nameBtn={t('documentsToRequire.dialogCancelButton')}
            onClickBtn={handleCloseModal}
          />
          <ButtonHLPure
            primary
            nameBtn={t('documentsToRequire.saveNewDocument')}
            onClickBtn={handleSaveGroups}
          />
        </div>
      </div>
    </InteractiveDialog>
  )
}

export { DocumentsToRequireClassifyModal as default } 
